import type { AuthFormProps } from '../provider/types';

import { use, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Checkbox, LoadingSpinner } from '@sgcmpteltd/uikit-react';
import { parsePhoneNumberWithError } from 'libphonenumber-js';

import { LoginContext } from '../provider/context';
import { CustomButton } from '@/components/common/custom-button';
import { CustomOtpInput } from '@/components/common/otp-input';
import { useRecaptchaSubmission } from '@/components/common/recaptcha/hooks';

import { useResponseDialog } from '@/hooks/use-response-dialog';
import { cn } from '@/lib/utils';

export const OtpVerify = () => {
  const {
    hasOtpRetryAttempts,
    isOtpCodeInvalid,
    isSubmittingLogin,
    isVerifyingOtp,
    nextOtpResendableRemainSeconds,
    onChangeOtp,
    onResendOtp,
    otpCode,
    recaptchaRef,
  } = use(LoginContext);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<AuthFormProps>();

  const { addDialog } = useResponseDialog();

  const { onSubmitFormWithRecaptcha } = useRecaptchaSubmission({
    onErrorCallback: () => {
      addDialog({
        message:
          'Something went wrong while verifying reCAPTCHA. Please try again.',
        title: 'Error',
        type: 'error',
      });
    },
    onSuccessCallback: (token) => {
      setValue('recaptcha_token', token);
    },
    recaptchaRef,
  });

  const mobile = watch('mobile');
  const email = watch('email');
  const isOtpCodeNotFilled = useMemo(() => {
    return Boolean(otpCode.includes(undefined));
  }, [otpCode]);
  const formattedMobile = useMemo(() => {
    try {
      const phoneNumber = parsePhoneNumberWithError(mobile, 'SG');

      return phoneNumber && phoneNumber.isValid()
        ? phoneNumber.formatInternational()
        : mobile;
    } catch {
      // console.error(error);
      return mobile;
    }
  }, [mobile]);

  const onResend = async () => {
    const isRecaptchaValid = await onSubmitFormWithRecaptcha();
    if (!isRecaptchaValid) {
      return;
    }

    onResendOtp();
  };

  return (
    <main
      className={cn(
        'rounded-[0.6rem] bg-white',
        'w-full md:max-w-[46.5rem]',
        'mx-auto md:mt-24',
        'p-16 md:py-[8.8rem] max-md:pt-[7.55rem]',
      )}
    >
      <div className="mx-auto flex flex-col gap-[1.4rem] max-md:max-w-[29.5rem]">
        <div className="text-center text-xl font-semibold">
          OTP Verification
        </div>
        {isVerifyingOtp ? (
          <div className="flex justify-center">
            <LoadingSpinner size={48} />
          </div>
        ) : (
          <>
            <div className="text-center text-base">
              <p>Please enter the OTP sent to</p>
              <ul className="list-none">
                <li>
                  <span className="font-semibold">{formattedMobile}</span>
                  <span> and</span>
                </li>
                <li>
                  <span className="font-semibold">{email}</span>
                </li>
              </ul>
            </div>
            <div>
              <CustomOtpInput
                countdown={nextOtpResendableRemainSeconds}
                errors={errors}
                hasOtpRetryAttempts={hasOtpRetryAttempts}
                isOtpCodeInvalid={isOtpCodeInvalid ?? false}
                onOtpCodeChange={onChangeOtp}
                onResend={onResend}
                otpCode={otpCode}
              />
            </div>
            <CustomButton
              className={cn('w-full', 'py-[0.9rem]')}
              disabled={isSubmittingLogin || isOtpCodeNotFilled}
              type="submit"
              variant="contained"
            >
              Submit
            </CustomButton>
            <Controller
              control={control}
              name="skip_next_time"
              render={({ field: { onChange, value } }) => (
                <div className="mx-auto flex items-center gap-4 py-[1.1rem]">
                  <Checkbox
                    checked={value === 1}
                    onChange={() => onChange(value === 1 ? 0 : 1)}
                    size={18}
                    value={1}
                  />
                  <span
                    onClick={() => onChange(value === 1 ? 0 : 1)}
                    onKeyDown={(e) => {
                      e.preventDefault();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Don't ask again for 120 days
                  </span>
                </div>
              )}
            />
          </>
        )}
      </div>
    </main>
  );
};
